import { effect, signal } from "@preact/signals";
import { readEntryContext } from "../config.js";
import { strings } from "../i18n/strings.es-AR.js";
import {
  startConversation, sendChatMessage, sendChatMedia, getChatHistory, getChatStatus,
  forgetDeviceRegistration
} from "../api/chat.js";
import { setSessionToken, isSessionError } from "../api/client.js";
import { openEventStream, closeEventStream, markSeen, wasSeen } from "../api/events.js";
import {
  getStableSessionId, getStoredConversation, storeConversation, clearConversation, rememberEntrySource,
  rotateSessionId
} from "../state/persistence.js";
import { loadLocalHistory, saveLocalHistory, clearLocalHistory } from "../state/localHistory.js";
import { mergeHistoryPage } from "../state/threadMerge.js";
import {
  messages, conversation, agentName, phase, sending, inFlightSends, uploading,
  historyCursor, loadingOlder, historyScope,
  appendMessage, updateMessage, nextKey, mergeConversationPayload
} from "../state/store.js";
import { track } from "../metrics/track.js";
import { syncPushSubscription } from "../push/push.js";
import { getTurnstileToken } from "../antibot/turnstile.js";

// entry es un signal para que los componentes que lo leen en render
// (ChatScreen, banners) re-rendericen cuando initConversation lo resuelve.
const entry = signal({ source: "direct", mode: "standalone", ctx: null, plink: null, auth: null });

export function getEntry() {
  return entry.value;
}

export async function initConversation() {
  entry.value = readEntryContext();
  rememberEntrySource(entry.value.source);
  track("session_start", { source: entry.value.source, mode: entry.value.mode });

  // Hilo local (hasta 30 días): pintado inmediato y a salvo de expiraciones
  // de sesión/conversación en el server. Nunca se limpia al reiniciar.
  messages.value = loadLocalHistory();
  startHistoryPersistence();

  // Con credenciales frescas (Mi Flux o link de un solo uso) siempre se
  // arranca conversación nueva autenticada; si no, se intenta rehidratar la previa.
  const stored = entry.value.auth || entry.value.plink ? null : getStoredConversation();
  if (stored) {
    setSessionToken(stored.sessionToken);
    try {
      const history = await getChatHistory(stored.convId);
      hydrateFromHistory(stored.convId, history);
      await refreshStatus(stored.convId);
      startEventStream(stored.convId, stored.sessionToken);
      phase.value = "ready";
      // Con permiso ya otorgado, re-registra la suscripción push (refresca
      // conversation_id y revive filas limpiadas por endpoints muertos).
      syncPushSubscription(stored.convId).catch(() => {});
      return;
    } catch {
      // Sesión vencida (401) o conversación expirada (404): empezar de nuevo.
      clearConversation();
      setSessionToken(null);
    }
  }

  await startNew();
}

/**
 * Arranca una conversación nueva.
 * @param {Object} [opts]
 * @param {boolean} [opts.silent] Renovación transparente de sesión (token
 *   vencido): no repite la bienvenida ni el aviso de link, porque el usuario
 *   está en medio de la conversación y no pidió empezar de cero. Tampoco mueve
 *   `phase`: bajarlo a "booting" deshabilita el composer y los botones (todos
 *   piden phase === "ready"), y un "error" reemplaza el hilo por la pantalla de
 *   fallo — justo lo que una renovación transparente tiene que evitar.
 * @returns {Promise<boolean>} true si quedó una sesión usable.
 */
export async function startNew({ silent = false } = {}) {
  if (!silent) phase.value = "booting";
  // El hilo local NO se limpia: la conversación nueva continúa el mismo hilo
  // visible (los mensajes previos quedan estampados con su convId anterior).
  try {
    const res = await startConversation({
      sessionId: getStableSessionId(),
      source: entry.value.source,
      auth: entry.value.auth,
      linkToken: entry.value.plink,
      // Token anti-bot de un solo uso (null sin site key configurada).
      turnstileToken: await getTurnstileToken()
    });
    setSessionToken(res.sessionToken);
    storeConversation(res.conversationId, res.sessionToken);
    conversation.value = {
      id: res.conversationId,
      state: null,
      isVerified: Boolean(res.authenticated),
      clientId: null,
      clientName: res.clientName || null,
      identificationRequired: !res.authenticated,
      handoffActive: false
    };
    // El link ya se había usado o venció: avisar antes de la bienvenida. Si
    // igual abrió la cuenta (sin verificar), el aviso es otro: no hace falta
    // identificarse, sólo validar para lo sensible.
    if (!silent && (res.linkReused || res.linkExpired || res.linkError)) {
      appendMessage({
        key: nextKey(),
        sender: "bot",
        text: res.linkReused ? strings.linkReused : res.linkExpired ? strings.linkExpired : strings.linkError,
        ts: Date.now()
      });
    }
    if (!silent && res.message) {
      // El saludo de identificación viene con sus opciones ("Soy cliente",
      // "Quiero contratar", "Planes y precios"): mismos botones que en WhatsApp.
      // La bienvenida del cliente ya identificado trae el menú principal (lista).
      appendMessage({
        key: nextKey(),
        sender: "bot",
        text: res.message,
        buttons: Array.isArray(res.buttons) && res.buttons.length ? res.buttons : null,
        list: res.list && typeof res.list === "object" ? res.list : null,
        ts: Date.now()
      });
    }
    // Conversación reanudada por el server: no manda bienvenida porque el hilo
    // ya venía empezado. Si el hilo local está vacío (historial purgado, otro
    // navegador), traer el transcript para no dejar la pantalla en blanco.
    if (res.resumed && messages.value.length === 0) {
      const history = await getChatHistory(res.conversationId).catch(() => null);
      // Se revisa de nuevo que el hilo siga vacío: una renovación silenciosa no
      // bloquea el composer, así que el usuario pudo mandar un mensaje durante
      // el fetch — y hydrateFromHistory descarta los mensajes que todavía no
      // están estampados con un convId. Si el hilo ya no está vacío, se cayó la
      // única razón para traer el transcript.
      if (history && messages.value.length === 0) hydrateFromHistory(res.conversationId, history);
    }
    // remembered cubre dispositivo registrado y teléfono recordado.
    if (res.authenticated || res.resumed || res.remembered) await refreshStatus(res.conversationId);
    startEventStream(res.conversationId, res.sessionToken);
    // Backfill de mensajes asíncronos (agente/CSAT) que llegaron sin stream
    // abierto: el replay SSE sólo repone a partir de ?lastEventId, y un stream
    // nuevo no tiene marcador. Dedupe por id contra lo ya visible.
    if (res.resumed) pollForAsyncMessages().catch(() => {});
    phase.value = "ready";
    syncPushSubscription(res.conversationId).catch(() => {});
    track("conversation_started", {
      resumed: Boolean(res.resumed),
      authenticated: Boolean(res.authenticated),
      deviceRegistered: Boolean(res.deviceRegistered)
    });
    return true;
  } catch (err) {
    if (!silent) phase.value = "error";
    track("api_error", { endpoint: "start", code: err?.code || null });
    return false;
  }
}

// ── Persistencia local del hilo (30 días) ──────────────────────────────────

let historyPersistenceStarted = false;
function startHistoryPersistence() {
  if (historyPersistenceStarted) return;
  historyPersistenceStarted = true;
  let timer = null;
  effect(() => {
    const snapshot = messages.value;
    const convId = conversation.value.id;
    if (timer) clearTimeout(timer);
    // Debounce: los estados transitorios (sending→sent) no valen un write cada uno.
    timer = setTimeout(() => saveLocalHistory(snapshot, convId), 300);
  });
}

// Revoca el registro del dispositivo (cookie httpOnly). Fire-and-forget: se
// usa junto con "cerrar sesion" para que otra cuenta no herede la identidad.
export function forgetDevice() {
  const convId = conversation.value.id;
  if (!convId) return;
  forgetDeviceRegistration(convId).catch(() => {});
}

/**
 * Cierra la sesión de la cuenta y deja el chat en blanco.
 *
 * El hilo se borra de verdad (es un dispositivo posiblemente compartido): no
 * alcanza con vaciar la pantalla, porque el historial vive en tres lugares y
 * cualquiera de los tres lo repone al recargar.
 *  1. El bot: "cerrar sesion" dispara cambiar_cuenta, que del lado del server
 *     cancela el handoff, olvida al cliente recordado y resetea el estado. Se
 *     manda PRIMERO, sobre la conversación vieja, para que esa limpieza corra.
 *  2. localStorage: historial local (30 días) + convId/token.
 *  3. El transcript del server: se deja atrás rotando el sessionId, así el
 *     /start siguiente abre una conversación nueva en vez de reanudar la vieja.
 */
export async function closeSession() {
  forgetDevice();
  await sendText("cerrar sesion");

  closeEventStream();
  clearLocalHistory();
  clearConversation();
  setSessionToken(null);
  messages.value = [];
  conversation.value = {
    id: null, state: null, isVerified: false, clientId: null, clientName: null,
    identificationRequired: true, handoffActive: false
  };
  agentName.value = null;
  // El dispositivo puede ser compartido: el scroll hacia arriba no puede
  // seguir teniendo cursor al historial de la cuenta que se acaba de cerrar.
  historyCursor.value = null;
  historyScope.value = "conversation";

  rotateSessionId();
  await startNew();
}

// ── Eventos asíncronos (SSE): agente humano, CSAT, handoff ────────────────

function startEventStream(convId, token) {
  const knownIds = messages.value.map((m) => m.id).filter(Boolean);
  openEventStream(convId, token, {
    initialSeenIds: knownIds,
    // El mensaje más nuevo que ya se tiene: desde ahí repone el server si el
    // stream se cae o se suspende antes de haber recibido su primer evento.
    initialLastEventId: knownIds[knownIds.length - 1] || null,
    onEvent: handleBusEvent,
    onFallbackPoll: pollForAsyncMessages,
    onAuthError: renewSession
  });
}

/**
 * Renueva la sesión de forma transparente: /start reemite sessionToken (y
 * reanuda la conversación si todavía vive del lado del server) y reabre el
 * stream. silent:true para no repetir la bienvenida en medio de la conversación.
 *
 * El token tiene TTL deslizante de 30 min y sólo lo refrescan los requests: el
 * SSE lo valida UNA vez, al conectar, y su heartbeat sólo refresca la presencia.
 * Así la sesión vence sola en dos situaciones normales, sin que la pestaña se
 * entere: con el navegador minimizado (el stream se suspende a propósito, para
 * que el server no vea presencia y mande el Web Push) y con la pestaña abierta
 * pero inactiva más de 30 min (el stream sigue conectado y nunca reautentica).
 *
 * Tres disparadores comparten esta única renovación: el 4xx del SSE, la vuelta
 * del background y el 401 de un envío. El vuelo único es obligatorio, no una
 * optimización: al volver de un background largo los tres detectan el
 * vencimiento casi juntos, y dos /start en paralelo con el mismo sessionId
 * abrirían dos conversaciones.
 *
 * El otro guard, `authErrorNotified` (en events.js), es contra otra cosa:
 * reintentos sucesivos del SSE. Sólo se rehabilita cuando un stream abre de
 * verdad, así un SSE roto de raíz no genera una conversación nueva por
 * reconexión.
 *
 * @returns {Promise<boolean>} true si quedó una sesión usable.
 */
let renewal = null;
function renewSession() {
  if (renewal) return renewal;
  renewal = (async () => {
    clearConversation();
    setSessionToken(null);
    return startNew({ silent: true });
  })().finally(() => { renewal = null; });
  return renewal;
}

// ── Vuelta del background ───────────────────────────────────────────────────
// Al volver a `visible` se revalida la sesión ANTES de que el usuario escriba,
// así el primer envío sale con token bueno. Sin esto la renovación dependía de
// que el SSE fallara primero: el envío podía adelantarse a ese fallo y, si la
// pestaña nunca se ocultó, el stream sigue conectado y el fallo no llega nunca.
// El GET además refresca el estado de la conversación, que pudo cambiar mientras
// la pestaña estaba en background (un operador que tomó el caso, por ejemplo).
const REVALIDATE_THROTTLE_MS = 10_000;
let lastRevalidateAt = 0;

async function revalidateSession() {
  const convId = conversation.value.id;
  // Con una renovación en curso no hay nada que revalidar, y el throttle evita
  // un GET por cada alt-tab cuando el usuario cambia de ventana seguido.
  if (!convId || renewal) return;
  const now = Date.now();
  if (now - lastRevalidateAt < REVALIDATE_THROTTLE_MS) return;
  lastRevalidateAt = now;
  try {
    applyStatus(convId, await getChatStatus(convId));
    // Mensajes del agente/CSAT llegados mientras la pestaña estaba oculta: el
    // replay del SSE sólo cubre desde el último id visto; si el stream de antes
    // no había visto ninguno, no repone nada. El poll dedupea por id.
    await pollForAsyncMessages();
  } catch (err) {
    // Sólo el vencimiento se atiende acá: un fallo de red lo reintenta el
    // propio envío del usuario, y el banner de offline ya lo comunica.
    if (isSessionError(err)) await renewSession();
  }
}

if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
  document.addEventListener("visibilitychange", () => {
    // Devuelve la promesa: el DOM ignora el valor de retorno, pero así la
    // revalidación es observable desde los tests.
    if (document.visibilityState === "visible") return revalidateSession();
  });
}

function handleBusEvent(type, payload) {
  const data = payload?.data || payload || {};
  if (type === "agent_message") {
    if (data.agentName) agentName.value = data.agentName;
    conversation.value = { ...conversation.value, handoffActive: true };
    appendMessage({
      key: nextKey(),
      id: data.id || null,
      sender: "agent",
      text: data.text || "",
      attachment: data.attachment || null,
      ts: Date.now()
    });
    track("agent_message_received", {});
  } else if (type === "bot_message") {
    // Mensaje del bot fuera del ciclo request/response (ej. validación de
    // identidad iniciada por un operador durante un handoff, o un documento).
    appendMessage({
      key: nextKey(),
      id: data.id || null,
      sender: "bot",
      text: data.text || "",
      buttons: data.buttons || null,
      attachment: data.attachment || data.metadata?.attachment || null,
      ts: Date.now()
    });
  } else if (type === "csat_request") {
    appendMessage({
      key: nextKey(),
      id: data.id || null,
      sender: "bot",
      text: data.text || "",
      csat: { options: data.suggestions || ["1", "3", "5"] },
      ts: Date.now()
    });
  } else if (type === "handoff") {
    conversation.value = { ...conversation.value, handoffActive: Boolean(data.active) };
    if (!data.active) agentName.value = null;
  }
}

// Red de seguridad si el SSE está caído: repone mensajes de agente/CSAT
// desde el historial, dedupeando por id.
async function pollForAsyncMessages() {
  const convId = conversation.value.id;
  if (!convId) return;
  try {
    const history = await getChatHistory(convId);
    for (const m of history?.messages || []) {
      if (!m.id || wasSeen(m.id)) continue;
      // `external_agent` = asesor externo (S1): para el cliente es un mensaje
      // de agente como cualquier otro.
      if (m.sender === "agent" || m.sender === "external_agent") {
        markSeen(m.id);
        handleBusEvent("agent_message", {
          data: { id: m.id, text: m.text, attachment: m.metadata?.attachment || null }
        });
      } else if (m.intent === "csat_survey") {
        markSeen(m.id);
        handleBusEvent("csat_request", { data: { id: m.id, text: m.text, suggestions: m.suggestions } });
      }
    }
  } catch {
    // sin conexión: el próximo tick reintenta
  }
}

export async function voteCsat(messageKey, score) {
  updateMessage(messageKey, { csatVote: score });
  track("csat_submitted", { score });
  await sendText(String(score));
}

// ── Upload de comprobantes ─────────────────────────────────────────────────

// Errores del server al subir un archivo, traducidos a mensajes accionables
// (antes caían al genérico "no se pudo enviar" y el usuario no sabía por qué).
function uploadErrorText(code) {
  if (code === "MEDIA_TOO_LARGE") return strings.attachTooBig;
  if (code === "UNSUPPORTED_MEDIA_TYPE") return strings.attachUnsupported;
  if (code === "QUOTA_EXCEEDED") return strings.attachQuotaExceeded;
  return null;
}

export async function sendMedia({ fileName, mimeType, dataBase64, caption }) {
  if (uploading.value || !conversation.value.id) return;
  uploading.value = true;
  const key = appendMessage({
    key: nextKey(),
    sender: "user",
    text: caption?.trim() || strings.attachmentSent(fileName),
    // isAttachment evita que el retry reenvíe el texto de la burbuja como
    // mensaje de chat: los bytes ya no están en memoria.
    isAttachment: true,
    status: "sending",
    ts: Date.now()
  });
  // Mismo criterio que deliver(): sesión vencida → renovar y reintentar una
  // vez. Acá el reintento es posible porque los bytes siguen en scope; en el
  // store no viven, y por eso retryMessage no reintenta adjuntos.
  const upload = () => sendChatMedia(conversation.value.id, { fileName, mimeType, dataBase64, caption });
  try {
    try {
      applyUploaded(key, await upload(), dataBase64);
    } catch (err) {
      if (isSessionError(err) && await renewSession()) applyUploaded(key, await upload(), dataBase64);
      else throw err;
    }
  } catch (err) {
    const reason = uploadErrorText(err?.code);
    updateMessage(key, { status: "failed", ...(reason ? { error: reason } : {}) });
    track("file_upload", { ok: false, code: err?.code || null });
    throw err;
  } finally {
    uploading.value = false;
  }
}

function applyUploaded(key, payload, dataBase64) {
  updateMessage(key, {
    status: "sent",
    ...(payload?.attachment ? { attachment: payload.attachment } : {})
  });
  applyResponse(payload);
  track("file_upload", { ok: true, bytes: dataBase64.length });
}

/**
 * Envía un texto del usuario. NO espera a que termine el envío anterior: el
 * cliente que manda tres frases cortas seguidas ("hola", "soy Ana", "quiero mi
 * factura") tiene que poder mandarlas, y el bridge las junta en un solo
 * procesamiento con su buffer de debounce por usuario — igual que en WhatsApp.
 *
 * Antes acá había un `if (sending.value) return` que descartaba el texto EN
 * SILENCIO: con el composer trabado durante todo el round trip (que incluye los
 * ~3 s de debounce del buffer) nunca llegaban dos mensajes juntos al bridge, así
 * que el buffer del canal web no se usaba nunca, y lo que el usuario escribía
 * mientras esperaba se perdía sin quedar ni en el hilo.
 *
 * Los POST quedan en vuelo en paralelo a propósito: el bridge resuelve los
 * anteriores con una respuesta vacía en cuanto entra uno nuevo y manda la
 * respuesta combinada por el último. Serializarlos acá volvería a anular el
 * buffer (cada mensaje esperaría su propio flush).
 */
export async function sendText(text) {
  const trimmed = String(text || "").trim();
  if (!trimmed) return;
  const key = appendMessage({ key: nextKey(), sender: "user", text: trimmed, status: "sending", ts: Date.now() });
  await deliver(key, () => sendChatMessage(conversation.value.id, trimmed));
}

export async function retryMessage(key) {
  const msg = messages.value.find((m) => m.key === key);
  if (!msg || msg.status !== "failed" || sending.value) return;
  // Un adjunto fallido no se puede reintentar: los bytes no viven en el store.
  // (Antes se reenviaba el texto "Adjunto: foo.jpg" como consulta al bot.)
  if (msg.isAttachment) return;
  updateMessage(key, { status: "sending" });
  await deliver(key, () => sendChatMessage(conversation.value.id, msg.text));
}

/**
 * Envía y, si la sesión venció, la renueva y reintenta UNA vez.
 *
 * Este reintento es lo que hace que retomar la conversación después de un rato
 * no muestre un error: el token vence solo (ver renewSession) y antes eso caía
 * en "No se pudo enviar", con el agravante de que el botón de reintentar volvía
 * a usar el MISMO token vencido.
 *
 * `request` es un thunk que lee `conversation.value.id` recién al invocarse: el
 * reintento sale contra la conversación renovada, no contra la vieja. Si el
 * /start abrió una conversación nueva (la anterior expiró con el mismo timeout
 * de 30 min), el mensaje entra ahí y el bot responde lo que corresponda a esa
 * sesión — pedir identificación de nuevo, por ejemplo.
 */
async function deliver(userMessageKey, request) {
  inFlightSends.value += 1;
  try {
    applyDelivered(userMessageKey, await request());
  } catch (err) {
    if (isSessionError(err) && await renewSession()) {
      try {
        applyDelivered(userMessageKey, await request());
        return;
      } catch (retryErr) {
        failDelivery(userMessageKey, retryErr);
        return;
      }
    }
    failDelivery(userMessageKey, err);
  } finally {
    inFlightSends.value -= 1;
  }
}

function applyDelivered(userMessageKey, payload) {
  updateMessage(userMessageKey, { status: "sent" });
  applyResponse(payload);
}

function failDelivery(userMessageKey, err) {
  updateMessage(userMessageKey, { status: "failed" });
  track("message_failed", { code: err?.code || null });
}

function applyResponse(payload) {
  if (!payload) return;

  const richMessages = Array.isArray(payload.messages) && payload.messages.length
    ? payload.messages
    : (payload.response ? [{ text: payload.response }] : []);

  for (const m of richMessages) {
    if (!m?.text) continue;
    appendMessage({
      key: nextKey(),
      sender: "bot",
      text: m.text,
      buttons: m.buttons || null,
      list: m.list || null,
      // Documentos del bot (cupón de pago, factura): la referencia viaja en
      // metadata y la card ofrece la descarga.
      attachment: m.metadata?.attachment || null,
      ts: Date.now()
    });
  }

  conversation.value = mergeConversationPayload(conversation.value, payload);
  if (payload.intent) track("intent_resolved", { intent: payload.intent });
}

// Estado de la conversación (abonado identificado, handoff, etc.) — se usa
// al rehidratar y tras un /start autenticado/reanudado.
async function refreshStatus(conversationId) {
  try {
    applyStatus(conversationId, await getChatStatus(conversationId));
  } catch {
    // sin status no se bloquea nada: los POST siguientes lo completan
  }
}

function applyStatus(conversationId, status) {
  // Validar la identidad desbloquea el hilo del abonado en otros dispositivos:
  // se vuelve a pedir la primera página para que el server recalcule el alcance
  // y devuelva el cursor hacia atrás (antes de validar no había nada que traer,
  // así que el cursor estaba en null y el scroll no pedía más).
  const justVerified = Boolean(status.isVerified) && !conversation.value.isVerified;

  conversation.value = {
    ...conversation.value,
    id: conversationId,
    // Igual que en applyResponse: un state null del server es un valor, no
    // un "no vino" — pisa el anterior.
    state: status.state === undefined ? conversation.value.state : status.state,
    isVerified: Boolean(status.isVerified),
    clientId: status.clientId || null,
    clientName: status.clientId ? (status.clientName || conversation.value.clientName) : null,
    identificationRequired: Boolean(status.identificationRequired),
    handoffActive: Boolean(status.handoffActive)
  };

  if (justVerified) {
    getChatHistory(conversationId)
      .then((history) => hydrateFromHistory(conversationId, history))
      .catch(() => {
        // El hilo actual ya está en pantalla; el historial ampliado se
        // recupera solo en el próximo refreshStatus o al recargar.
      });
  }
}

/** Proyecta los mensajes de una página del server al formato del store. */
function toThreadMessages(convId, history) {
  const items = Array.isArray(history?.messages) ? history.messages : [];
  return items
    .filter((m) => m.text)
    .map((m) => ({
      key: nextKey(),
      id: m.id || null,
      // Los tramos recuperados de otro dispositivo traen su conversación de
      // origen; los de la actual, la actual.
      convId: m.conversationId || convId,
      // `external_agent` es el asesor de un proveedor externo (S1): para el
      // cliente es una persona atendiéndolo, igual que un agente del equipo.
      sender: m.sender === "customer" ? "user" : (m.sender === "agent" || m.sender === "external_agent" ? "agent" : "bot"),
      text: m.text,
      buttons: m.buttons || null,
      list: m.list || null,
      attachment: m.metadata?.attachment || null,
      isAttachment: Boolean(m.metadata?.attachment) && m.sender === "customer",
      csat: m.intent === "csat_survey" ? { options: m.suggestions?.length ? m.suggestions : ["1", "3", "5"] } : undefined,
      status: m.sender === "customer" ? "sent" : undefined,
      ts: m.timestamp || Date.now()
    }));
}

/**
 * Primera página del historial: la más reciente. Deja el cursor listo para que
 * el scroll hacia arriba siga trayendo hacia atrás.
 */
function hydrateFromHistory(convId, history) {
  messages.value = mergeHistoryPage(messages.value, toThreadMessages(convId, history));
  historyCursor.value = history?.nextBefore || null;
  historyScope.value = history?.scope === "customer" ? "customer" : "conversation";
  conversation.value = { ...conversation.value, id: convId };
}

/**
 * Trae la página anterior y la prepende. La llama MessageList al acercarse el
 * scroll al tope.
 *
 * @returns {Promise<boolean>} true si se agregaron mensajes (la vista tiene que
 *   compensar el salto de altura para no moverle el hilo al usuario).
 */
export async function loadOlderMessages() {
  const convId = conversation.value.id;
  const cursor = historyCursor.value;
  if (!convId || !cursor || loadingOlder.value) return false;

  loadingOlder.value = true;
  try {
    const page = await getChatHistory(convId, cursor);
    const older = toThreadMessages(convId, page);
    messages.value = mergeHistoryPage(messages.value, older);
    // Una página vacía corta la paginación aunque el server siga mandando
    // cursor: el efecto que rellena el viewport se re-dispara con cada cambio
    // de cursor, y sin este corte quedaría pidiendo la misma página para
    // siempre.
    historyCursor.value = older.length ? (page?.nextBefore || null) : null;
    track("history_page_loaded", { count: older.length, scope: historyScope.value });
    return older.length > 0;
  } catch {
    // Se deja el cursor intacto: el próximo intento del usuario reintenta la
    // misma página en vez de saltearla.
    return false;
  } finally {
    loadingOlder.value = false;
  }
}
